import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Instagram, Music2 } from "lucide-react";
import { CONFIG, IMAGES } from "../../lib/config";

export default function Nokuji() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section
      id="nokuji"
      ref={ref}
      data-testid="nokuji-section"
      className="relative overflow-hidden border-t border-white/10 bg-[#0c0c0e] py-24 lg:py-40"
    >
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 items-center gap-14 px-5 lg:grid-cols-12 lg:px-10">
        {/* Clipped spotlight image */}
        <div className="lg:col-span-6">
          <div className="relative mx-auto aspect-[9/12] max-w-md overflow-hidden">
            <motion.img
              style={{ y: imgY, scale: 1.18 }}
              src={IMAGES.nokuji}
              alt="Nokuji en DJ set"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
            <div className="absolute left-0 top-6 bg-[#FF0000] px-4 py-1.5 font-mono-x text-[10px] uppercase tracking-[0.25em] text-white">
              1er DJ set
            </div>
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="mb-5 font-mono-x text-xs uppercase tracking-[0.3em] text-[#FF0000]"
          >
            [ Tête d’affiche ]
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="font-display text-6xl uppercase leading-[0.85] tracking-tight text-white lg:text-8xl"
          >
            NOKUJI
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400 lg:text-lg"
          >
            Créatif touche-à-tout suivi par une communauté fidèle, Nokuji monte pour la première
            fois derrière les platines. Un set inédit, imprévisible, pensé pour Éclipse. Un moment
            qui n’arrivera qu’une seule fois.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href={CONFIG.nokuji.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nokuji-tiktok"
              className="group flex items-center gap-2 border border-white/20 bg-white/5 px-6 py-3 font-mono-x text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-[#FF0000] hover:text-[#FF0000]"
            >
              <Music2 className="h-4 w-4" /> TikTok
            </a>
            <a
              href={CONFIG.nokuji.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nokuji-instagram"
              className="group flex items-center gap-2 border border-white/20 bg-white/5 px-6 py-3 font-mono-x text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-[#FF0000] hover:text-[#FF0000]"
            >
              <Instagram className="h-4 w-4" /> Instagram
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
